import { formatDate as formatDateUtil, formatDateTime as formatDateTimeUtil } from '../../utils/dateUtils';
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Edit,
  Trash2,
  Plus,
  Camera,
  Video,
  FileText,
  Calendar,
  User,
  MapPin,
  GraduationCap,
  DollarSign,
  Heart,
  CheckCircle,
  AlertCircle,
  Clock,
  Shield,
  Eye,
} from 'lucide-react';
import { useGetChildDetailsQuery, useDeleteChildMutation, useVerifyChildMutation, useGetSponsorshipUpdatesQuery, useDeleteSponsorshipUpdateMutation } from '../../store/slices/apiSlice';
import toast from 'react-hot-toast';
import AddUpdateModal from '../../components/admin/AddUpdateModal';
import EditUpdateModal from '../../components/admin/EditUpdateModal';
import ProfilePreviewModal from '../../components/admin/ProfilePreviewModal';

// Note: SponsorshipUpdate type is imported from apiSlice

const ChildDetails: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [deleteChild] = useDeleteChildMutation();
  const [verifyChild] = useVerifyChildMutation();
  const [deleteUpdate] = useDeleteSponsorshipUpdateMutation();

  // Debug: Log the ID parameter
  console.log('ChildDetails - ID parameter:', id);
  console.log('ChildDetails - ID type:', typeof id);
  console.log('ChildDetails - Parsed ID:', parseInt(id!));

  // Ensure ID is valid before making API call
  const childId = id ? parseInt(id) : null;
  const isValidId = childId && !isNaN(childId) && childId > 0;

  console.log('ChildDetails - Valid ID check:', { childId, isValidId });
  console.log('ChildDetails - Will skip API call:', !isValidId);

  const { data: childResponse, isLoading, error, refetch } = useGetChildDetailsQuery(childId!, {
    skip: !isValidId, // Skip the query if ID is not valid
    // Force refetch when component mounts
    refetchOnMountOrArgChange: true
  });
  const child = childResponse?.data;

  // Debug: Log the API response
  console.log('ChildDetails - API Response:', childResponse);
  console.log('ChildDetails - Loading state:', isLoading);
  console.log('ChildDetails - Error state:', error);

  // Force API call when component mounts or ID changes
  useEffect(() => {
    if (isValidId && childId && !childResponse) {
      console.log('ChildDetails - Force refetching data for child ID:', childId);
      refetch();
    }
  }, [childId, isValidId]); // Removed refetch from dependencies to avoid infinite loop

  const [activeTab, setActiveTab] = useState('overview');
  const [isAddUpdateModalOpen, setIsAddUpdateModalOpen] = useState(false);
  const [isEditUpdateModalOpen, setIsEditUpdateModalOpen] = useState(false);
  const [selectedUpdate, setSelectedUpdate] = useState<any>(null);
  const [isProfilePreviewOpen, setIsProfilePreviewOpen] = useState(false);

  // Fetch updates for this child
  const { data: updatesResponse, isLoading: updatesLoading, error: updatesError } = useGetSponsorshipUpdatesQuery({
    page: 1,
    limit: 100,
    search: child?.name || ''
  });

  // Debug: Log the child data to see what we're getting (remove after testing)
  if (import.meta.env.DEV) {
    console.log('ChildDetails - child data:', child);
    console.log('ChildDetails - funding_options:', child?.funding_options);
    console.log('ChildDetails - funding_requirements:', child?.funding_requirements);
    console.log('ChildDetails - updatesResponse:', updatesResponse);
    console.log('ChildDetails - sponsor data:', {
      status: child?.status,
      sponsor_id: child?.sponsor_id,
      sponsor_name: child?.sponsor_name,
      sponsor_email: child?.sponsor_email,
      sponsor_phone: child?.sponsor_phone,
      sponsorship_start_date: child?.sponsorship_start_date
    });
  }

  // Handle the response structure properly - check if data is an array or if it's nested
  const updates = React.useMemo(() => {
    try {
      if (!updatesResponse) return [];

      // Check if data is directly an array
      if (Array.isArray(updatesResponse.data)) {
        return updatesResponse.data.filter((update: any) => update.child?.child_id === parseInt(id!));
      }

      // Check if data has a nested array (like data.updates or data.results)
      if (updatesResponse.data && typeof updatesResponse.data === 'object') {
        const possibleArrays = ['updates', 'results', 'items', 'list'];
        for (const key of possibleArrays) {
          const dataKey = (updatesResponse.data as any)[key];
          if (Array.isArray(dataKey)) {
            const filteredUpdates = dataKey.filter((update: any) => update.child?.child_id === parseInt(id!));
            console.log('Filtered updates for child', id, ':', filteredUpdates);
            return filteredUpdates;
          }
        }
      }

      return [];
    } catch (error) {
      console.error('Error processing updates response:', error);
      return [];
    }
  }, [updatesResponse, id]);


  const handleDeleteChild = async () => {
    if (window.confirm('Are you sure you want to delete this child? This action cannot be undone.')) {
      try {
        await deleteChild({ child_id: parseInt(id!) }).unwrap();
        toast.success('Child deleted successfully');
        navigate('/admin/child-sponsorship');
      } catch (error: any) {
        toast.error((error as any)?.data?.message || 'Failed to delete child');
      }
    }
  };

  const handleVerifyChild = async () => {
    if (!child) return;

    try {
      await verifyChild({
        child_id: parseInt(id!),
        verified: !child.is_verified
      }).unwrap();
      toast.success('Child verification updated successfully');
    } catch (error: any) {
      toast.error((error as any)?.data?.message || 'Failed to update child verification');
    }
  };

  const handleEditUpdate = (update: any) => {
    // Validate update object before opening modal
    if (!update || !update.update_id || !update.child || !update.child.child_id) {
      console.error('Invalid update data:', update);
      toast.error('Invalid update data. Please refresh and try again.');
      return;
    }

    console.log('Opening edit modal for update:', update);

    // Transform the update object to match EditUpdateModal expectations
    const transformedUpdate = {
      ...update,
      child_id: update.child.child_id // Extract child_id from nested child object
    };

    setSelectedUpdate(transformedUpdate);
    setIsEditUpdateModalOpen(true);
  };

  const handleDeleteUpdate = async (update: any) => {
    if (window.confirm('Are you sure you want to delete this update? This action cannot be undone.')) {
      try {
        await deleteUpdate({ update_id: update.update_id }).unwrap();
        toast.success('Update deleted successfully');
      } catch (error: any) {
        toast.error((error as any)?.data?.message || 'Failed to delete update');
      }
    }
  };

  // Get currency symbol based on child's currency
  const getCurrencySymbol = (currencyCode: string) => {
    const currencySymbols: { [key: string]: string } = {
      'NGN': '₦',
      'USD': '$',
      'GBP': '£',
      'INR': '₹',
      'CAD': 'C$',
      'AUD': 'A$',
      'EUR': '€',
      'CHF': 'CHF',
      'SEK': 'kr',
      'NOK': 'kr',
      'DKK': 'kr',
      'JPY': '¥',
      'KRW': '₩',
      'CNY': '¥',
      'HKD': 'HK$',
      'SGD': 'S$',
      'MYR': 'RM',
      'THB': '฿',
      'IDR': 'Rp',
      'PHP': '₱',
      'VND': '₫',
      'ZAR': 'R',
      'EGP': 'E£',
      'KES': 'KSh',
      'GHS': 'GH₵',
      'MAD': 'MAD',
      'TND': 'DT',
      'DZD': 'DA',
      'LYD': 'LD',
      'SDG': 'SDG',
      'ETB': 'Br',
      'UGX': 'USh',
      'TZS': 'TSh',
      'RWF': 'RF',
      'XOF': 'CFA',
      'XAF': 'FCFA',
      'CDF': 'FC',
      'STD': 'Db',
      'AOA': 'Kz',
      'ZMW': 'ZK',
      'ZWL': 'Z$',
      'BWP': 'P',
      'NAD': 'N$',
      'SZL': 'L',
      'LSL': 'L',
      'MWK': 'MK',
      'MZN': 'MT',
      'MGA': 'Ar',
      'MUR': '₨',
      'SCR': '₨',
      'KMF': 'CF',
      'DJF': 'Fdj',
      'SOS': 'S',
      'ERN': 'Nfk',
      'SSP': 'SS£',
      'BIF': 'FBu',
      'SLE': 'Le',
      'LRD': 'L$',
      'GNF': 'FG',
      'GMD': 'D',
      'CVE': '$',
      'MRU': 'UM'
    };

    return currencySymbols[currencyCode] || currencyCode || '₦';
  };

  const formatCurrency = (amount: number) => {
    const currencyCode = child?.currency_code || 'NGN';
    const currency = currencyCode;

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
    }).format(amount).replace(/[A-Z]{3}/, getCurrencySymbol(currencyCode));
  };

  const formatDate = (dateString: string | Date | null | undefined) => formatDateUtil(dateString);;

  const calculateProgress = (raised: number | undefined, target: number | undefined) => {
    if (!raised || !target) return 0;
    return Math.min((raised / target) * 100, 100);
  };

  const getStatusBadge = (status: string | undefined) => {
    switch (status) {
      case 'available':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
            <Clock className="w-4 h-4 mr-1" />
            Available
          </span>
        );
      case 'sponsored':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
            <Heart className="w-4 h-4 mr-1" />
            Sponsored
          </span>
        );
      case 'graduated':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
            <GraduationCap className="w-4 h-4 mr-1" />
            Graduated
          </span>
        );
      default:
        return null;
    }
  };

  const getUpdateTypeIcon = (update_type: string) => {
    switch (update_type) {
      case 'progress_report':
        return <FileText className="w-5 h-5 text-blue-600" />;
      case 'photo_update':
        return <Camera className="w-5 h-5 text-green-600" />;
      case 'video_update':
        return <Video className="w-5 h-5 text-purple-600" />;
      case 'general_update':
        return <FileText className="w-5 h-5 text-orange-600" />;
      default:
        return <FileText className="w-5 h-5 text-gray-600" />;
    }
  };

  const getUpdateTypeColor = (update_type: string) => {
    switch (update_type) {
      case 'progress_report':
        return 'bg-blue-100 text-blue-800';
      case 'photo_update':
        return 'bg-green-100 text-green-800';
      case 'video_update':
        return 'bg-purple-100 text-purple-800';
      case 'general_update':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    console.log('ChildDetails - Loading state active');
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading child details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    console.log('ChildDetails - Error state active:', error);
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Error Loading Child</h2>
          <p className="text-gray-600 mb-6">
            {(error as any)?.data?.message || 'There was an error loading the child details. Please try again.'}
          </p>
          <div className="space-x-4">
            <button
              onClick={() => refetch()}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Retry
            </button>
            <button
              onClick={() => navigate('/admin/child-sponsorship')}
              className="text-blue-600 hover:text-blue-800"
            >
              ← Back to Child Sponsorship
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!child) {
    console.log('ChildDetails - No child data found');
    console.log('ChildDetails - Child response:', childResponse);
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Child Not Found</h2>
          <p className="text-gray-600 mb-6">The child you're looking for doesn't exist.</p>
          <div className="space-x-4">
            <button
              onClick={() => refetch()}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Retry
            </button>
            <button
              onClick={() => navigate('/admin/child-sponsorship')}
              className="text-blue-600 hover:text-blue-800"
            >
              ← Back to Child Sponsorship
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Final fallback - this should never be reached
  console.log('ChildDetails - Rendering main component');
  console.log('ChildDetails - Final state:', {
    child,
    isLoading,
    error,
    childResponse,
    childId,
    isValidId
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin/child-sponsorship')}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{child.name}</h1>
                <p className="text-gray-600">Child Profile & Sponsorship Details</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {getStatusBadge(child.status)}
              <button
                onClick={handleVerifyChild}
                className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${child.is_verified
                    ? 'bg-green-100 text-green-800 hover:bg-green-200'
                    : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                  }`}
              >
                <Shield className="w-4 h-4 mr-1" />
                {child.is_verified ? 'Verified' : 'Verify'}
              </button>
              <button
                onClick={() => navigate(`/admin/child-sponsorship/edit/${child.child_id}`)}
                className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Edit className="w-4 h-4 mr-1" />
                Edit
              </button>
              <button
                onClick={handleDeleteChild}
                className="flex items-center px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 py-6">
        <div className="max-w-7xl mx-auto">
          {/* Tabs */}
          <div className="bg-white rounded-lg shadow mb-6">
            <div className="border-b border-gray-200">
              <nav className="flex space-x-8 px-6">
                {[
                  { id: 'overview', label: 'Overview', icon: User },
                  { id: 'updates', label: 'Updates', icon: FileText },
                  { id: 'sponsor', label: 'Sponsor Info', icon: Heart }
                ].map((tab) => {
                  const IconComponent = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === tab.id
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                      <IconComponent className="w-4 h-4 mr-2" />
                      {tab.label}
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Child Profile */}
              <div className="lg:col-span-2 space-y-6">
                <div className="bg-white rounded-lg shadow">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">Child Profile</h2>
                  </div>
                  <div className="p-6">
                    <div className="flex items-start space-x-6">
                      <div
                        className="w-32 h-32 bg-gray-200 rounded-full flex items-center justify-center overflow-hidden cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => setIsProfilePreviewOpen(true)}
                        title="Click to view profile preview"
                      >
                        {child.profile_image ? (
                          <img
                            src={child.profile_image}
                            alt={child.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <User className="w-16 h-16 text-gray-400" />
                        )}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">{child.name}</h3>
                        <div className="space-y-2 text-gray-600">
                          <div className="flex items-center">
                            <Calendar className="w-4 h-4 mr-2" />
                            Age {child.age}
                          </div>
                          <div className="flex items-center">
                            <MapPin className="w-4 h-4 mr-2" />
                            {child.location}
                          </div>
                          <div className="flex items-center">
                            <GraduationCap className="w-4 h-4 mr-2" />
                            {child.education_stage}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="mt-6">
                      <h4 className="font-semibold text-gray-900 mb-2">About {child.name}</h4>
                      <p className="text-gray-700 leading-relaxed">{child.bio}</p>
                    </div>
                  </div>
                </div>

                {/* Funding Information */}
                <div className="bg-white rounded-lg shadow">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">Funding Information</h2>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                      <div className="text-center p-6 bg-white border border-gray-200 rounded-xl">
                        <DollarSign className="w-8 h-8 mx-auto mb-2 text-gray-600" />
                        <p className="text-sm text-gray-600">Weekly</p>
                        <p className="text-xl font-bold text-gray-900">{getCurrencySymbol(child?.currency_code || 'NGN')}{(child.funding_options?.weekly || child.funding_requirements?.weekly || 0).toLocaleString()}</p>
                      </div>
                      <div className="text-center p-6 bg-white border border-gray-200 rounded-xl">
                        <DollarSign className="w-8 h-8 mx-auto mb-2 text-gray-600" />
                        <p className="text-sm text-gray-600">Monthly</p>
                        <p className="text-xl font-bold text-gray-900">{getCurrencySymbol(child?.currency_code || 'NGN')}{(child.funding_options?.monthly || child.funding_requirements?.monthly || 0).toLocaleString()}</p>
                      </div>
                      <div className="text-center p-6 bg-white border border-gray-200 rounded-xl">
                        <DollarSign className="w-8 h-8 mx-auto mb-2 text-gray-600" />
                        <p className="text-sm text-gray-600">Yearly</p>
                        <p className="text-xl font-bold text-gray-900">{getCurrencySymbol(child?.currency_code || 'NGN')}{(child.funding_options?.yearly || child.funding_requirements?.yearly || 0).toLocaleString()}</p>
                      </div>
                    </div>

                    {child.status === 'sponsored' && (
                      <div>
                        <div className="flex justify-between text-sm text-gray-600 mb-2">
                          <span>Progress</span>
                          <span>{formatCurrency(child.total_raised || 0)} / {formatCurrency(child.target_amount || 0)}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div
                            className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                            style={{ width: `${calculateProgress(child.total_raised, child.target_amount)}%` }}
                          ></div>
                        </div>
                        <p className="text-sm text-gray-500 mt-2">
                          {calculateProgress(child.total_raised, child.target_amount).toFixed(1)}% funded
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Quick Stats */}
                <div className="bg-white rounded-lg shadow">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">Quick Stats</h2>
                  </div>
                  <div className="p-6 space-y-4">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Status</span>
                      {getStatusBadge(child.status)}
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Verified</span>
                      <span className={`flex items-center ${child.is_verified ? 'text-green-600' : 'text-yellow-600'}`}>
                        {child.is_verified ? <CheckCircle className="w-4 h-4 mr-1" /> : <AlertCircle className="w-4 h-4 mr-1" />}
                        {child.is_verified ? 'Yes' : 'No'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Added</span>
                      <span className="text-gray-900">{formatDate(child.created_at)}</span>
                    </div>
                    {child.status === 'sponsored' && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Sponsored Since</span>
                          <span className="text-gray-900">{formatDate(child.sponsorship_start_date)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Last Update</span>
                          <span className="text-gray-900">{formatDate(child.last_update_date)}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="bg-white rounded-lg shadow">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">Actions</h2>
                  </div>
                  <div className="p-6 space-y-3">
                    <button
                      onClick={() => setIsAddUpdateModalOpen(true)}
                      className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Update
                    </button>
                    <button
                      onClick={() => setActiveTab('updates')}
                      className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      View All Updates
                    </button>
                    <button
                      onClick={() => navigate(`/admin/child-sponsorship/edit/${child.child_id}`)}
                      className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Edit Profile
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'updates' && (
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-gray-900">Sponsorship Updates</h2>
                    <button
                      onClick={() => setIsAddUpdateModalOpen(true)}
                      className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Update
                    </button>
                  </div>
                </div>
                <div className="p-6">
                  {updatesLoading ? (
                    <div className="text-center py-12">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                      <p className="text-gray-600">Loading updates...</p>
                    </div>
                  ) : updatesError ? (
                    <div className="text-center py-12">
                      <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">Error loading updates</h3>
                      <p className="text-gray-600 mb-4">There was an error loading the updates. Please try again.</p>
                    </div>
                  ) : updates.length === 0 ? (
                    <div className="text-center py-12">
                      <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No updates yet</h3>
                      <p className="text-gray-600 mb-4">Start by adding the first update for this child.</p>
                      <button
                        onClick={() => setIsAddUpdateModalOpen(true)}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        Add First Update
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {updates.map((update: any) => (
                        <div key={update.update_id} className="border border-gray-200 rounded-lg p-6">
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex items-center space-x-3">
                              {getUpdateTypeIcon(update.update_type)}
                              <div>
                                <h3 className="text-lg font-semibold text-gray-900">{update.title}</h3>
                                <p className="text-sm text-gray-600">{formatDate(update.created_at)}</p>
                              </div>
                            </div>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getUpdateTypeColor(update.update_type)}`}>
                              {update.update_type.charAt(0).toUpperCase() + update.update_type.slice(1)}
                            </span>
                          </div>

                          <p className="text-gray-700 mb-4">{update.description}</p>

                          {update.content && (
                            <div className="mb-4">
                              <h4 className="text-sm font-medium text-gray-900 mb-2">Content</h4>
                              <p className="text-gray-700 whitespace-pre-wrap">{update.content}</p>
                            </div>
                          )}

                          {update.images && update.images.length > 0 && (
                            <div className="mb-4">
                              <h4 className="text-sm font-medium text-gray-900 mb-2">Images</h4>
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                {update.images.map((image: any, index: number) => (
                                  <div key={index} className="aspect-w-16 aspect-h-9 bg-gray-200 rounded-lg overflow-hidden">
                                    <img src={image} alt={`Update ${update.update_id} image ${index + 1}`} className="w-full h-full object-cover" />
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {update.videos && update.videos.length > 0 && (
                            <div className="mb-4">
                              <h4 className="text-sm font-medium text-gray-900 mb-2">Videos</h4>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {update.videos.map((video: any, index: number) => (
                                  <div key={index} className="aspect-w-16 aspect-h-9 bg-gray-200 rounded-lg overflow-hidden">
                                    <video src={video} controls className="w-full h-full" />
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}


                          <div className="flex items-center justify-end space-x-2">
                            <button
                              onClick={() => handleEditUpdate(update)}
                              className="flex items-center px-3 py-1 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteUpdate(update)}
                              className="flex items-center px-3 py-1 text-sm text-red-600 hover:text-red-800 transition-colors"
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'sponsor' && (
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Sponsor Information</h2>
              </div>
              <div className="p-6">
                {(() => {
                  // Get the first active sponsorship from the sponsorships array
                  const activeSponsorship = child.sponsorships && child.sponsorships.length > 0
                    ? child.sponsorships.find(sponsor => sponsor.status === 'active') || child.sponsorships[0]
                    : null;

                  console.log('ChildDetails - Sponsor Info Debug:', {
                    childStatus: child.status,
                    sponsorships: child.sponsorships,
                    activeSponsorship
                  });

                  return (child.status === 'sponsored' && activeSponsorship) ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">{activeSponsorship.sponsor_name || 'No Sponsor Name'}</h3>
                        <div className="space-y-3">
                          <div className="flex items-center">
                            <User className="w-4 h-4 mr-3 text-gray-400" />
                            <span className="text-gray-600">Sponsorship ID: {activeSponsorship.sponsorship_id || 'N/A'}</span>
                          </div>
                          <div className="flex items-center">
                            <Calendar className="w-4 h-4 mr-3 text-gray-400" />
                            <span className="text-gray-600">Sponsored since: {formatDate(activeSponsorship.start_date)}</span>
                          </div>
                          <div className="flex items-center">
                            <DollarSign className="w-4 h-4 mr-3 text-gray-400" />
                            <span className="text-gray-600">Amount: {getCurrencySymbol(child?.currency_code || 'NGN')}{activeSponsorship.amount} ({activeSponsorship.sponsorship_type})</span>
                          </div>
                          <div className="flex items-center">
                            <Calendar className="w-4 h-4 mr-3 text-gray-400" />
                            <span className="text-gray-600">End Date: {formatDate(activeSponsorship.end_date)}</span>
                          </div>
                        </div>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-3">Contact Information</h4>
                        <div className="space-y-2">
                          <p className="text-sm text-gray-600">Email: {activeSponsorship.sponsor_email || 'N/A'}</p>
                          <p className="text-sm text-gray-600">Sponsorship Type: {activeSponsorship.sponsorship_type || 'N/A'}</p>
                          <p className="text-sm text-gray-600">Status: <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${activeSponsorship.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>{activeSponsorship.status}</span></p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Heart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No Sponsor Yet</h3>
                      <p className="text-gray-600 mb-4">
                        {child.status === 'sponsored'
                          ? 'This child is marked as sponsored but no sponsor information is available.'
                          : 'This child is not currently sponsored. They are available for sponsorship.'
                        }
                      </p>
                      {child.status !== 'sponsored' && (
                        <button
                          onClick={() => {
                            // You can add functionality to assign a sponsor here
                            toast.info('Contact admin to assign a sponsor to this child');
                          }}
                          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          Assign Sponsor
                        </button>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add Update Modal */}
      <AddUpdateModal
        isOpen={isAddUpdateModalOpen}
        onClose={() => setIsAddUpdateModalOpen(false)}
        childId={parseInt(id!)}
        childName={child?.name || ''}
      />

      {/* Edit Update Modal */}
      {selectedUpdate && (
        <EditUpdateModal
          isOpen={isEditUpdateModalOpen}
          onClose={() => {
            setIsEditUpdateModalOpen(false);
            setSelectedUpdate(null);
          }}
          update={selectedUpdate}
        />
      )}

      {/* Profile Preview Modal */}
      {child && (
        <ProfilePreviewModal
          isOpen={isProfilePreviewOpen}
          onClose={() => setIsProfilePreviewOpen(false)}
          child={child}
        />
      )}
    </div>
  );
};

export default ChildDetails;
